import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ChevronRight, Trophy, User, Users } from 'lucide-react'
import { useApp } from '../../context/AppContext'
import { NeonCity } from '../stages'

export function ArcadePageShell({ children, Background = NeonCity }) {
  const { state, tabs } = useApp()
  const location = useLocation()
  const currentUser = state.users.find(u => u.id === state.currentUserId)

  return (
    <div className="relative min-h-screen overflow-y-auto bg-gray-950 p-6 text-white lg:overflow-hidden lg:p-10">
      <div className="pointer-events-none absolute inset-0 z-0">
        <Background className="h-full w-full scale-105 object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-950/95 to-gray-900/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-gray-950/40" />
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-[1600px] flex-col gap-6 lg:min-h-[calc(100vh-5rem)]">
        <header className="flex w-full shrink-0 flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <div className="flex flex-col">
            <h1 className="text-4xl font-black uppercase italic tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400 drop-shadow-lg md:text-5xl">
              Cache Smash
            </h1>
            <p className="mt-1 text-sm font-bold uppercase tracking-widest text-cyan-400">
              A browser-native platform fighter
            </p>
          </div>

          <div className="flex flex-wrap gap-3 text-xs font-bold uppercase tracking-wider">
            <div className="flex items-center gap-2 rounded-full border border-white/10 bg-gray-900/80 px-4 py-2 shadow-lg">
              <User size={14} className="text-cyan-400" />
              <span>{currentUser?.name || 'Guest'}</span>
            </div>
            <div className="hidden items-center gap-2 rounded-full border border-white/10 bg-gray-900/80 px-4 py-2 shadow-lg sm:flex">
              <span className="h-2 w-2 rounded-full bg-green-500" />
              <span>Online</span>
            </div>
            <div className="hidden items-center gap-2 rounded-full border border-white/10 bg-gray-900/80 px-4 py-2 shadow-lg md:flex">
              <Users size={14} className="text-gray-400" />
              <span>{state.characters.length} Fighters</span>
            </div>
            <div className="hidden items-center gap-2 rounded-full border border-white/10 bg-gray-900/80 px-4 py-2 shadow-lg lg:flex">
              <Trophy size={14} className="text-yellow-400" />
              <span>{state.tournaments.length} Tournaments</span>
            </div>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-6 lg:flex-row lg:gap-10">
          <motion.nav
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
            }}
            className="flex w-full shrink-0 flex-row gap-2 overflow-x-auto pb-2 lg:w-64 lg:flex-col lg:overflow-visible lg:pb-0 lg:pt-12"
          >
            {tabs.map(([id, label]) => {
              const to = id === 'home' ? '/' : `/${id}`
              const isActive = location.pathname === to

              return (
                <motion.div
                  key={id}
                  variants={{
                    hidden: { opacity: 0, x: -20 },
                    visible: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } },
                  }}
                >
                  <Link
                    to={to}
                    className={`group relative flex shrink-0 items-center px-4 py-3 text-left transition-all duration-300 lg:w-full ${isActive ? 'scale-105 text-white' : 'text-gray-500 hover:text-gray-200'}`}
                  >
                    <div className={`absolute left-0 top-0 h-1 w-full origin-top bg-cyan-500 transition-all duration-300 lg:h-full lg:w-1 lg:origin-left ${isActive ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`} />
                    <div className="absolute inset-0 hidden bg-gradient-to-r from-white/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 lg:block" />
                    <span className="relative z-10 whitespace-nowrap text-xl font-black uppercase italic tracking-wider lg:text-2xl">
                      {label}
                    </span>
                    {isActive && <ChevronRight className="relative z-10 ml-auto hidden text-cyan-500 lg:block" size={24} />}
                  </Link>
                </motion.div>
              )
            })}
          </motion.nav>

          <main className="flex min-w-0 flex-1 flex-col gap-4">
            {children}
          </main>
        </div>
      </div>
    </div>
  )
}
